import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Rocket } from 'lucide-react';
import { registerParent, googleLogin } from '../api/studentApi';
import { trackEvent, EVENT_SIGNUP_COMPLETED } from '../utils/analytics';
import './Register.css';
import { useUser } from '../context/UserContext';
import { GoogleLogin } from '@react-oauth/google';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const { loginUser } = useUser(); // Access the loginUser function from context

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      // Call the existing backend google-login endpoint
      const res = await googleLogin(credentialResponse.credential);

      const data = res.data;

      // Save token and session info
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      loginUser(data.user); // Update context with logged-in user
      trackEvent(EVENT_SIGNUP_COMPLETED, { method: 'google_signup' });

      // If you track new registrations vs existing users, route accordingly:
      // e.g. navigate('/onboarding') or directly to dashboard
      navigate('/parent-dashboard');
    } catch (err) {
      console.error('Google registration error:', err);
      const message =
        err.response?.data?.message || 'Google signup failed. Please try again.';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleFailure = () => {
    setErrorMessage('Google Sign-up was cancelled or encountered an error.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await registerParent(formData);
      console.log("Success!", response.data);
      trackEvent(EVENT_SIGNUP_COMPLETED, { method: 'parent_registration' });
      navigate('/login');
    } catch (err) {
      alert(err.response?.data?.message || "Registration failed");
    }
  };

  return (
    <div className="auth-container">
      {/* LEFT SIDE: Visual/Hero (Hidden on mobile) */}
      <div className="onboarding-hero">
        <div className="floating-rocket">
          <Rocket size={80} color="white" fill="white" />
        </div>

        <h1>Ready to spark your child's learning adventure? 🌟</h1>

        <div className="steps-container">
          <div className="step-card">
            <span className="step-number">1</span>
            <p>Create your <strong>parent account</strong> today to get started.</p>
          </div>

          <div className="step-card">
            <span className="step-number">2</span>
            <p>Set up your <strong>kids' profiles</strong> so they can collect coins, earn badges, and track progress!</p>
          </div>
        </div>

        <div className="stats-row">
          <div className="stat-item">
            <strong>10k+</strong>
            <span>Students</span>
          </div>
          <div className="stat-item">
            <strong>500+</strong>
            <span>Quizzes</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE: The Form */}
      <div className="auth-form-section">
        <div className="form-box">
          {/* Mobile Only Brand Header */}
          <div className="mobile-brand-header">
            <div className="mobile-rocket-badge">
              <Rocket size={24} color="#4f46e5" fill="#4f46e5" />
            </div>
            <span className="brand-name">Knolup</span>
          </div>

          <div className="form-header">
            <h2>Create Parent Account</h2>
            <p>Already have an account? <Link to="/login">Login here</Link></p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <User className="input-icon" size={20} />
              <input
                type="text"
                placeholder="Full Name"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </div>

            <div className="input-group">
              <Mail className="input-icon" size={20} />
              <input
                type="email"
                placeholder="Email Address"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="input-group">
              <Lock className="input-icon" size={20} />
              <input
                type="password"
                placeholder="Password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <button type="submit" className="btn primary-btns btn-full">
              Join Now
            </button>
          </form>

          <div className="google-login-wrapper">
            <div className="google-login-section">
              <span className="google-login-text">Only for parents</span>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleFailure}
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                width="100%"
              />

            </div>
          </div>

          <p className="terms-text">
            By joining, you agree to our <a href="/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;